  // src/pages/Contact/Contact.content.js
  import {
    Container,
    Row,
    Col,
    Form,
    FormGroup,
    Label,
    Input,
    Button,
    Card,
    CardBody,
    Alert,
  } from "reactstrap";
  import { useTranslation } from "react-i18next";
  import { useState, useMemo, useRef } from "react";
  import emailjs from "emailjs-com";
  import {
    FaFacebook,
    FaTwitter,
    FaLinkedin,
    FaInstagram,
    FaEnvelope,
  } from "react-icons/fa";

  export const ContactContent = () => {
    const { t } = useTranslation();

    const [formData, setFormData] = useState({
      name: "",
      email: "",
      subject: "",
      message: "",
      consent: false,
      _gotcha: "",
    });

    const [submitting, setSubmitting] = useState(false);
    const [alert, setAlert] = useState({ type: "", msg: "" });

    const emailValid = useMemo(() => {
      if (!formData.email) return false;
      return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(formData.email);
    }, [formData.email]);

    const nameValid = (formData.name || "").trim().length > 0;
    const subjectValid = !!formData.subject;
    const messageValid = (formData.message || "").trim().length > 0;
    const consentValid = !!formData.consent;

    const formValid =
      emailValid && nameValid && subjectValid && messageValid && consentValid;

    const handleChange = (e) => {
      const { name, value, type, checked } = e.target;
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    };

    const resetForm = () => {
      setFormData({
        name: "",
        email: "",
        subject: "",
        message: "",
        consent: false,
        _gotcha: "",
      });
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      setAlert({ type: "", msg: "" });

      if (formData._gotcha) return;

      if (!formValid) {
        setAlert({
          type: "warning",
          msg: t("Por favor, revisa los campos resaltados."),
        });
        return;
      }

      try {
        setSubmitting(true);

        // Fecha/hora para {{submitted_at}}
        let submittedAt = new Date().toLocaleString("es-GT", {
          timeZone: "America/Guatemala",
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        });
        if (!submittedAt || typeof submittedAt !== "string") {
          submittedAt = new Date().toISOString();
        }

        const templateParams = {
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
          submitted_at: submittedAt,
          reply_to: formData.email,
        };

        const res = await emailjs.send(
          process.env.REACT_APP_EMAILJS_SERVICE_ID,
          process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
          templateParams,
          process.env.REACT_APP_EMAILJS_PUBLIC_KEY
        );

        console.log("EmailJS OK:", res);
        setAlert({
          type: "success",
          msg: t("¡Gracias! Tu mensaje ha sido enviado correctamente."),
        });
        resetForm();
      } catch (err) {
        console.error("EmailJS ERROR:", {
          text: err?.text,
          message: err?.message,
          status: err?.status,
          err,
        });
        setAlert({
          type: "danger",
          msg: t("Ocurrió un error al enviar el mensaje. Inténtalo de nuevo."),
        });
      } finally {
        setSubmitting(false);
      }
    };

    const baseBtnStyle = {
      padding: "10px 30px",
      fontWeight: 600,
      letterSpacing: "0.5px",
      boxShadow: "0 4px 15px rgba(0, 123, 255, 0.3)",
      transition: "all 0.3s ease",
      border: "none",
      borderRadius: "8px",
      backgroundColor: "#0d6efd",
    };

    const hoverBtnStyle = {
      transform: "translateY(-2px)",
      boxShadow: "0 6px 20px rgba(0, 123, 255, 0.4)",
      backgroundColor: "#0b5ed7",
    };

    const teamCardStyle = {
      backgroundColor: "#ffffff",
      border: "1px solid #dee2e6",
      borderRadius: "10px",
      padding: "30px 20px",
      height: "100%",
      boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
      transition: "all 0.3s ease",
    };
    const onCardOver = (e) => {
      e.currentTarget.style.transform = "translateY(-5px)";
      e.currentTarget.style.boxShadow = "0 5px 20px rgba(0,0,0,0.15)";
    };
    const onCardOut = (e) => {
      e.currentTarget.style.transform = "translateY(0)";
      e.currentTarget.style.boxShadow = "0 2px 10px rgba(0,0,0,0.1)";
    };

    const btnRef = useRef(null);

    return (
      <div
        style={{
          paddingTop: "60px",
          paddingBottom: "60px",
          backgroundColor: "#f8f9fa",
        }}
      >
        <Container>
          {/* Título principal */}
          <Row className="mb-5">
            <Col>
              <h1
                className="text-center contact-title"
                style={{
                  fontWeight: "bold",
                  marginBottom: "1rem",
                }}
              >
                {t("CONTÁCTANOS")}
              </h1>
              <p className="text-center lead contact-subtitle">
                {t("¿Tienes preguntas sobre el CEC 2026? ¡Estamos aquí para ayudarte!")}
              </p>
            </Col>
          </Row>

          {/* Dos columnas principales */}
          <Row className="g-4 align-items-stretch">
            {/* Información de contacto */}
            <Col lg={4} md={12} className="d-flex">
              <Card
                className="flex-fill h-100 d-flex"
                style={teamCardStyle}
                onMouseOver={onCardOver}
                onMouseOut={onCardOut}
              >
                <CardBody
                  className="d-flex flex-column justify-content-start"
                  style={{ paddingTop: "2rem" }}
                >
                  <h3 className="contact-title-card mb-4">
                    {t("Información de Contacto:")}
                  </h3>

                  {/* Correo electrónico con ícono azul */}
                  <div className="mb-4">
                    <div className="d-flex align-items-center">
                      <div className="contact-icon">
                        <FaEnvelope />
                      </div>
                      <div>
                        <strong>{t("Correo electrónico:")}</strong>
                        <br />
                        <a href="mailto:faq@cecspace.org" className="contact-mail">
                          faq@cecspace.org
                        </a>
                      </div>
                    </div>
                  </div>

                  <hr className="my-3" />

                  {/* Redes sociales */}
                  <div>
                    <h4 className="contact-subtitle-card">
                      {t("Síguenos en nuestras redes:")}
                    </h4>
                    <div className="d-flex gap-3 mt-2">
                      <a
                        href="https://www.facebook.com/cecspace/"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Facebook"
                        className="contact-social-link subtle"
                      >
                        <FaFacebook />
                      </a>
                      <a
                        href="https://x.com/cec_space"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Twitter/X"
                        className="contact-social-link subtle"
                      >
                        <FaTwitter />
                      </a>
                      <a
                        href="https://www.linkedin.com/company/i-congreso-espacial-centroamericano/"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="LinkedIn"
                        className="contact-social-link subtle"
                      >
                        <FaLinkedin />
                      </a>
                      <a
                        href="https://www.instagram.com/cec_space"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Instagram"
                        className="contact-social-link subtle"
                      >
                        <FaInstagram />
                      </a>
                    </div>
                  </div>

                  {/* Logo del Congreso */}
                  <div className="mt-auto pt-4 d-flex justify-content-center">
                    <img
                      src="/assets/images/cec-logo.png"
                      alt="CEC Logo"
                      className="contact-logo-bottom"
                    />
                  </div>
                </CardBody>
              </Card>
            </Col>

            {/* Formulario */}
            <Col lg={8} md={12} className="d-flex">
              <Card
                className="flex-fill h-100 d-flex"
                style={teamCardStyle}
                onMouseOver={onCardOver}
                onMouseOut={onCardOut}
              >
                <CardBody
                  className="d-flex flex-column justify-content-start"
                  style={{ padding: "2rem", paddingTop: "2rem" }}
                >
                  <h3 className="contact-title-card mb-4">
                    {t("¡Envíanos un Mensaje!")}
                  </h3>

                  {alert.msg && (
                    <Alert color={alert.type} role="alert" aria-live="polite">
                      {alert.msg}
                    </Alert>
                  )}

                  <Form onSubmit={handleSubmit} noValidate className="mt-2">
                    {/* Honeypot */}
                    <div
                      style={{
                        position: "absolute",
                        left: "-10000px",
                        height: 0,
                        overflow: "hidden",
                      }}
                      aria-hidden="true"
                    >
                      <Label htmlFor="_gotcha">Do not fill this field</Label>
                      <Input
                        type="text"
                        id="_gotcha"
                        name="_gotcha"
                        value={formData._gotcha}
                        onChange={handleChange}
                        tabIndex={-1}
                      />
                    </div>

                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label htmlFor="name" className="contact-label">
                            {t("Nombre completo:")}{" "}
                            <span className="contact-required">*</span>
                          </Label>
                          <Input
                            type="text"
                            name="name"
                            id="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            aria-invalid={!nameValid}
                            className="contact-input"
                          />
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label htmlFor="email" className="contact-label">
                            {t("Correo electrónico:")}{" "}
                            <span className="contact-required">*</span>
                          </Label>
                          <Input
                            type="email"
                            name="email"
                            id="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            aria-invalid={!emailValid}
                            className="contact-input"
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <FormGroup>
                      <Label htmlFor="subject" className="contact-label">
                        {t("Asunto")}{" "}
                        <span className="contact-required">*</span>
                      </Label>
                      <Input
                        type="select"
                        name="subject"
                        id="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        aria-invalid={!subjectValid}
                        className="contact-input"
                      >
                        <option value="">
                          {t("Selecciona un asunto")}
                        </option>
                        <option value="Consulta General">
                          {t("Consulta General")}
                        </option>
                        <option value="Registro al Evento">
                          {t("Registro al Evento")}
                        </option>
                        <option value="Patrocinios">
                          {t("Patrocinios")}
                        </option>
                        <option value="Ponencias">
                          {t("Ponencias")}
                        </option>
                        <option value="Prensa y Medios">
                          {t("Prensa y Medios")}
                        </option>
                        <option value="Otro">
                          {t("Otro")}
                        </option>
                      </Input>
                    </FormGroup>

                    <FormGroup className="mb-3">
                      <Label htmlFor="message" className="contact-label">
                        {t("Mensaje")}{" "}
                        <span className="contact-required">*</span>
                      </Label>
                      <Input
                        type="textarea"
                        name="message"
                        id="message"
                        rows="6"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        aria-invalid={!messageValid}
                        className="contact-input"
                      />
                    </FormGroup>

                    <FormGroup check className="mb-4">
                      <Input
                        type="checkbox"
                        id="consent"
                        name="consent"
                        checked={formData.consent}
                        onChange={handleChange}
                        aria-invalid={!consentValid}
                      />
                      <Label htmlFor="consent" check className="contact-consent">
                        <strong>
                          {t("Acepto la")}{" "}
                          <a href="/privacy-policy" className="contact-link">
                            {t("política de privacidad")}
                          </a>{" "}
                          {t("y el uso de mis datos para dar seguimiento a mi solicitud.")}
                        </strong>{" "}
                        <span className="contact-required">*</span>
                      </Label>
                    </FormGroup>

                    <div className="d-flex justify-content-end">
                      <Button
                        innerRef={btnRef}
                        color="primary"
                        size="lg"
                        type="submit"
                        disabled={submitting || !formValid}
                        className="btn btn-primary"
                        style={baseBtnStyle}
                        onMouseOver={(e) => {
                          Object.assign(e.currentTarget.style, hoverBtnStyle);
                        }}
                        onMouseOut={(e) => {
                          Object.assign(e.currentTarget.style, {
                            transform: "translateY(0)",
                            boxShadow: baseBtnStyle.boxShadow,
                            backgroundColor: baseBtnStyle.backgroundColor,
                          });
                        }}
                      >
                        {submitting
                          ? t("Enviando…")
                          : t("Enviar Mensaje")}
                      </Button>
                    </div>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>

        {/* Estilos locales */}
        <style jsx="true">{`
          .contact-title { color: #333333 !important; }
          .contact-subtitle { color: #333333 !important; }
          .contact-title-card { color: #333333 !important; font-size: 1.5rem; font-weight: 600; text-shadow: none !important; filter: none !important; }
          .contact-subtitle-card { color: #2c3e50 !important; font-size: 1.05rem; font-weight: 500; margin-bottom: 0.5rem; text-shadow: none !important; filter: none !important; }

          .contact-icon {
            background-color: #007bff; color: #ffffff; font-size: 1rem;
            width: 36px; height: 36px; border-radius: 50%;
            display: flex; align-items: center; justify-content: center;
            margin-right: 0.75rem; box-shadow: 0 2px 5px rgba(0,0,0,0.15);
          }
          .contact-mail { color: #007bff; text-decoration: none; font-weight: 600; }
          .contact-mail:hover { text-decoration: underline; }

          .contact-social-link.subtle { color: #495057; font-size: 1.25rem; transition: color .2s ease, transform .2s ease; }
          .contact-social-link.subtle:hover { color: #007bff; transform: scale(1.1); }

          .contact-label { color: #333333 !important; font-weight: 500; }
          .contact-required { color: #dc3545; font-weight: bold; }
          .contact-link { color: #007bff; text-decoration: none; font-weight: 600; }
          .contact-link:hover { text-decoration: underline; }
          .contact-consent { color: #333333 !important; line-height: 1.6; }
          .contact-input { border-radius: 8px; }

          .contact-logo-bottom {
            height: 125px;
            width: auto;
            display: block;
            margin-top: 1rem;
            margin-bottom: 0.25rem;
            margin-left: 0;
            opacity: 0.9;
            transition: transform 0.3s ease;
          }
          .contact-logo-bottom:hover { transform: scale(1.05); }
        `}</style>
      </div>
    );
  };